import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReviewQueueItem } from '../../../../../../models/review-queue-item';
import { AutoUnsubscribe } from '../../../../../../decorators/auto-unsubscribe';
import { firstValueFrom, Subscription } from 'rxjs';
import { ReviewQueueService } from '../../../../../../services/review-queue.service';
import { NgxPopupComponent } from '../../../../../shared/ngx-popups/ngx-popups/ngx-popups';
import { Router } from '@angular/router';
import {
  HUMAN_INPUT_REQUIRED_STATUS,
  REVIEWED_STATUS,
  ERROR_STATUS,
  MALICIOUS_FILE_STATUS,
  FINAL_REVIEW_STEP,
  TABLE_ID_VALIDATION_STEP,
  TABLE_TABULATION_VALIDATION_STEP,
  TABLE_IDENTIFICATION_STEP,
  TABLE_TABULATION_STEP,
  SPREADING_STEP,
  ADDITIONAL_DETAILS_NEEDED_STEP,
  DOCUMENT_PASSWORD_ENTRY_REQUIRED,
  PREP_FOR_MANUAL_REVIEW_STEP,
  COMPLETE_STATUS,
  TABLE_AGGREGATION_STEP,
  TABLE_AGGREGATION_VALIDATION_STEP
} from '../../../../../../utils/constants';
import { DocumentFileService } from '@services/document-file.service';
import { DocumentFile } from '@models/document-file';
import { SharedDataService } from "@services/shared-data.service";
import { LoggingService, Logger } from '@services/logging.service';

@Component({
    selector: 'app-review-status-modal',
    templateUrl: './status-modal.component.html',
    styleUrls: ['./status-modal.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class StatusModalComponent implements OnInit, OnDestroy {
  @Input() popup: NgxPopupComponent; // MANDATORY
  @Input() reviewQueueItemId: string;
  @Input() fromStep: string = null;
  /**
   * While pending, polling continues but no redirect or error is acted on. Lets a step keep the modal open during
   * its own save without a stale status from the previous pass sending the user to the next screen (AUTO-4587).
   */
  @Input() holdRedirectUntil: Promise<unknown> = null;
  private isRedirectHeld = false;
  private isDestroyed = false;
  reviewQueueItem: ReviewQueueItem = null;
  error: string;
  timesChecked = 0;
  intervalCheckSeconds = 5;
  subsArr$: Subscription[] = [];
  transitionHeaders: { [key: string]: string } = {};
  private transitionHeadersLower: { [key: string]: string } = {};
  currentTransitionHeader = '';
  private fromStepModalStartTime: number = null;
  allProcessingIcons: string[] = ['fa-file-arrow-up', 'fa-object-ungroup', 'fa-list-ul', 'fa-list-check'];
  cyclingIconIndex = 0;
  get cyclingIcon(): string {
    return this.allProcessingIcons[this.cyclingIconIndex % this.allProcessingIcons.length] || this.allProcessingIcons[0];
  }

  private interval: ReturnType<typeof setInterval>;
  private iconCycleInterval: ReturnType<typeof setInterval>;
  logger: Logger;
  constructor(
    private _documentFileService: DocumentFileService,
    private _reviewQueueService: ReviewQueueService,
    private _sharedDataService: SharedDataService,
    private _router: Router,
    private _loggingService: LoggingService,
  ) {
    this.logger = this._loggingService.rootLogger.newLogger('StatusModalComponent');
  }

  ngOnInit() {
    this.initializeTransitionHeaders();
    this.updateTransitionHeader();
    if (this.fromStep) {
      this.fromStepModalStartTime = Date.now();
    }
    this.holdRedirectIfRequested();
    this.loadStatus();
    this.startStatusPolling();
    this.startIconCycling();
  }

  private holdRedirectIfRequested(): void {
    if (!this.holdRedirectUntil) {
      return;
    }
    this.isRedirectHeld = true;
    const releaseRedirect = () => {
      this.isRedirectHeld = false;
      if (!this.isDestroyed) {
        this.loadStatus();
      }
    };
    this.holdRedirectUntil.then(releaseRedirect, releaseRedirect);
  }

  private lookupTransitionHeader(step: string): string {
    return step ? this.transitionHeadersLower[step.trim().toLowerCase()] : undefined;
  }

  private initializeTransitionHeaders(): void {
    this.transitionHeaders[TABLE_AGGREGATION_STEP] = 'Cleaning Tables...';
    this.transitionHeaders[TABLE_AGGREGATION_VALIDATION_STEP] = 'Cleaning Tables...';
    this.transitionHeaders[TABLE_IDENTIFICATION_STEP] = 'Cleaning Tables...';
    this.transitionHeaders[TABLE_ID_VALIDATION_STEP] = 'Cleaning Tables...';
    this.transitionHeaders['TableAggregation'] = 'Cleaning Tables...';
    this.transitionHeaders['TableAggregationValidation'] = 'Cleaning Tables...';
    this.transitionHeaders['TableIdentification'] = 'Cleaning Tables...';
    this.transitionHeaders['TableIDValidation'] = 'Cleaning Tables...';
    this.transitionHeaders['TableCleaning'] = 'Cleaning Tables...';
    
    this.transitionHeaders[TABLE_TABULATION_STEP] = 'Prepping for Data Review...';
    this.transitionHeaders[TABLE_TABULATION_VALIDATION_STEP] = 'Prepping for Data Review...';
    this.transitionHeaders['TableTabulation'] = 'Prepping for Data Review...';
    this.transitionHeaders['TableTabulationValidation'] = 'Prepping for Data Review...';
    this.transitionHeaders[PREP_FOR_MANUAL_REVIEW_STEP] = 'Prepping for Data Review...';
    this.transitionHeaders['PrepForManualReview'] = 'Prepping for Data Review...';
    
    this.transitionHeaders[FINAL_REVIEW_STEP] = 'Categorizing Items...';
    this.transitionHeaders['ManualReview'] = 'Categorizing Items...';

    this.transitionHeadersLower = Object.keys(this.transitionHeaders).reduce((acc, key) => {
      acc[key.toLowerCase()] = this.transitionHeaders[key];
      return acc;
    }, {} as { [key: string]: string });
  }
  private startIconCycling(): void {
    const iconCycleMs = 2200; // 1.1s per bounce * 2 bounces
    this.iconCycleInterval = setInterval(() => {
      this.cyclingIconIndex = (this.cyclingIconIndex + 1) % this.allProcessingIcons.length;
    }, iconCycleMs);
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    clearInterval(this.interval);
    if (this.iconCycleInterval) {
      clearInterval(this.iconCycleInterval);
    }
  }

  goToLink(link: Array<string|number>) {
    clearInterval(this.interval);
    this.popup.closable = true;
    this.popup.close();
    this._router.navigate(link);
  }

  closeModal() {
    clearInterval(this.interval);
    if (this.iconCycleInterval) {
      clearInterval(this.iconCycleInterval);
    }
    this.popup.closable = true;
    this.popup.close();
  }

  startStatusPolling() {
    const numMilliSecondsInASecond = 1000;
    const intervalTime = this.intervalCheckSeconds * numMilliSecondsInASecond;
    this.interval = setInterval(() => {
      this.loadStatus();
      this.timesChecked++;
    }, intervalTime);
  }

  loadStatus() {
    const wasRequestedWhileRedirectHeld = this.isRedirectHeld;
    firstValueFrom(this._reviewQueueService.getItemStatus(this.reviewQueueItemId)).then(rqi => {
      if (this.isDestroyed || wasRequestedWhileRedirectHeld) {
        return;
      }
      this.reviewQueueItem = rqi;

      if (this.reviewQueueItem && this.reviewQueueItem.embeddedWorkflow) {
        this._sharedDataService.embeddedWorkflow$.next(this.reviewQueueItem.embeddedWorkflow);
      }

      this.surfaceTimeoutError();
      this.updateTransitionHeader();
      if (this.timesChecked > 0 || this.fromStep) {
        this.checkForRedirect();
      }
    }).catch(error => {
      this.logger.error('Status Modal: loadStatus error', { 'errorObject': error });
      this.error = error;
      if (this.iconCycleInterval) {
        clearInterval(this.iconCycleInterval);
      }
    })
  }

  private updateTransitionHeader(): void {
    const step = this.fromStep || this.reviewQueueItem?.step;
    this.currentTransitionHeader = this.lookupTransitionHeader(step) || 'Processing...';
  }

  surfaceTimeoutError(): void {
    const numTimesCheckedInTenMinutes = 600 / this.intervalCheckSeconds;
    if (this.timesChecked <= numTimesCheckedInTenMinutes) {
      return;
    }

    clearInterval(this.interval);
    if (this.error) {
      return;
    }

    this.error = 'Processing is taking longer than expected. Please close this modal, reload the page, and try again. If the problem persists, reach out to your administrator.';
    if (this.iconCycleInterval) {
      clearInterval(this.iconCycleInterval);
    }
  }

  isLoading(): boolean {
    if (!this.reviewQueueItem) {
      return true;
    }

    return ![HUMAN_INPUT_REQUIRED_STATUS, REVIEWED_STATUS, ERROR_STATUS, COMPLETE_STATUS, MALICIOUS_FILE_STATUS].includes(
      this.reviewQueueItem.status
    );
  }

  checkForRedirect() {
    if (this.isRedirectHeld) {
      return;
    }

    if (this.fromStep && this.fromStepModalStartTime) {
      const timeSinceModalOpened = Date.now() - this.fromStepModalStartTime;
      const minimumVisibilityTime = 1000; 
      if (timeSinceModalOpened < minimumVisibilityTime) {
        return;
      }
    }

    if (!this.isLoading()) {
      if ([ERROR_STATUS, MALICIOUS_FILE_STATUS].includes(this.reviewQueueItem.status)) {
        if (this.reviewQueueItem.userError || this.reviewQueueItem.status === MALICIOUS_FILE_STATUS) {
          this.error = this.reviewQueueItem.exceptionMessage;
        } else {
          this.error = 'There was an error trying to process your document'
        }
        clearInterval(this.interval);
        if (this.iconCycleInterval) {
          clearInterval(this.iconCycleInterval);
        }
        return;
      }
      if (this.reviewQueueItem.status === REVIEWED_STATUS || this.reviewQueueItem.step === SPREADING_STEP) {
        this.subsArr$.push(this._documentFileService.listDocumentFiles(null, this.reviewQueueItem.documentFileId).subscribe(files => {
          const documentFile: DocumentFile = files?.[0];
          if (documentFile) {
            this.goToLink(['spread', documentFile.uuid]);
          }
          return;
        }));
        return;
      }

      if (this.reviewQueueItem.status === HUMAN_INPUT_REQUIRED_STATUS && [ADDITIONAL_DETAILS_NEEDED_STEP].includes(this.reviewQueueItem.step)) {
        this.goToLink(['review', this.reviewQueueItem.id, 'confirm_details']);
      } else if (this.reviewQueueItem.status === HUMAN_INPUT_REQUIRED_STATUS && [DOCUMENT_PASSWORD_ENTRY_REQUIRED].includes(this.reviewQueueItem.step)) {
        this.goToLink(['review', this.reviewQueueItem.id, 'password_entry']);
      } else {
        switch (this.reviewQueueItem.step) {
          case TABLE_ID_VALIDATION_STEP:
            if (this.timesChecked > 10) { // had at least ten secs to make sure this isn't a cold start scenario
              // this shouldn't affect initial upload because it will probably take longer than ten seconds for
              // the entire document to get into the table identification validation step anyway.
              this.goToLink(['review', this.reviewQueueItem.id, 'table_identification']);
              return;
            }
            return;
          case TABLE_TABULATION_VALIDATION_STEP:
            if (this.fromStep !== TABLE_TABULATION_VALIDATION_STEP) {
              this.goToLink(['review', this.reviewQueueItem.id, 'table_tabulation']);
            }
            return;
          case FINAL_REVIEW_STEP:
            if (this.fromStep !== FINAL_REVIEW_STEP) {
              this.goToLink(['review', this.reviewQueueItem.id, 'manual_review']);
            }
            return;
        }

      }
    }
  }
}
